import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, DollarSign, Zap, Shield, Clock, ArrowRight } from 'lucide-react';
export function Pricing() {
  const [transactionAmount, setTransactionAmount] = useState(75);
  // Calculate fees based on transaction amount
  const stripeFee = transactionAmount * 0.029 + 0.3;
  const raceLookupFee = transactionAmount * 0.011;
  const totalFees = stripeFee + raceLookupFee;
  const payout = transactionAmount - totalFees;
  return <div className="w-full bg-gray-50 min-h-screen" data-id="element-1827">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E578E] to-[#01AAE7] py-16 sm:py-24 overflow-hidden" data-id="element-1828">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1829">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-1830"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-1831"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10 text-center" data-id="element-1832">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight" data-id="element-1833">
            RaceLookup: The Smart Choice for Race Registration
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto" data-id="element-1834">
            Are you tired of shelling out an arm and a leg for outdated race
            registration platforms? With RaceLookup, you get a comprehensive,
            affordable solution built by race timers and event planners. Whether
            you're organizing a 5K, marathon, or any other distance, RaceLookup
            is your one-stop shop for seamless race management.
          </p>
        </div>
      </section>

      {/* Early Adopter Pricing Section */}
      <section className="py-12 sm:py-16 lg:py-20" data-id="element-1835">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10" data-id="element-1836">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-1837">
            {/* Left Column - Description */}
            <div data-id="element-1838">
              <div className="mb-6" data-id="element-1839">
                <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4" data-id="element-1840">
                  EARLY ADOPTER PRICING
                </h2>
                <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg font-bold text-sm mb-6" data-id="element-1841">
                  GET IN ON THE GROUND FLOOR
                </div>
              </div>

              <div className="prose prose-lg" data-id="element-1842">
                <p className="text-gray-700 leading-relaxed mb-6" data-id="element-1843">
                  Simple, upfront pricing, and no contracts. Stop wasting your
                  budget on costly, clunky registration platforms. RaceLookup is
                  the obvious choice for anyone looking to streamline operations
                  and maximize profitability. Create a free member ID today and
                  see for yourself why race timers and event planners trust
                  RaceLookup for their events, big and small.
                </p>

                <div className="bg-blue-50 border-l-4 border-[#01AAE7] p-4 rounded-r-lg" data-id="element-1844">
                  <p className="text-sm text-gray-600 italic" data-id="element-1845">
                    *Credit card fees will only increase if your processor
                    (Stripe) has an increase. RaceLookup fees do not apply to
                    credit card fees.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 sticky top-8" data-id="element-1846">
              <h3 className="text-xl font-black text-gray-900 mb-6" data-id="element-1847">
                Compare for yourself!
              </h3>

              <p className="text-sm text-gray-600 mb-6" data-id="element-1848">
                Since 2.9% + $0.31 RaceLookup: 1.1%*
                <br data-id="element-1849" />
                Adjust the transaction amount to see the estimated processing
                costs. Enter a custom value or use the slider to simulate your
                cart fee.
              </p>

              {/* Transaction Amount Input */}
              <div className="mb-8" data-id="element-1850">
                <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-1851">
                  Transaction amount ($)
                </label>
                <input type="range" min="10" max="500" value={transactionAmount} onChange={e => setTransactionAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#01AAE7]" data-id="element-1852" />
                <div className="flex justify-between items-center mt-2" data-id="element-1853">
                  <input type="number" value={transactionAmount} onChange={e => setTransactionAmount(Number(e.target.value))} className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg text-center font-bold text-gray-900 focus:outline-none focus:border-[#01AAE7]" data-id="element-1854" />
                  <span className="text-sm text-gray-500" data-id="element-1855">$10 - $500</span>
                </div>
              </div>

              {/* Fee Comparison */}
              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1856">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-1857">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-1" data-id="element-1858">
                    Stripe Fees*
                  </div>
                  <div className="text-2xl font-black text-gray-900" data-id="element-1859">
                    ${stripeFee.toFixed(2)}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border border-[#01AAE7]" data-id="element-1860">
                  <div className="text-xs font-bold text-[#01AAE7] uppercase mb-1" data-id="element-1861">
                    RaceLookup Fees*
                  </div>
                  <div className="text-2xl font-black text-[#01AAE7]" data-id="element-1862">
                    ${raceLookupFee.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1863">
                <div className="bg-gray-100 rounded-xl p-4" data-id="element-1864">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-1" data-id="element-1865">
                    Total Fees
                  </div>
                  <div className="text-2xl font-black text-gray-900" data-id="element-1866">
                    ${totalFees.toFixed(2)}
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4 border border-green-500" data-id="element-1867">
                  <div className="text-xs font-bold text-green-700 uppercase mb-1" data-id="element-1868">
                    Payout
                  </div>
                  <div className="text-2xl font-black text-green-700" data-id="element-1869">
                    ${payout.toFixed(2)}
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 italic" data-id="element-1870">
                *Estimated using current RaceLookup processing rates (4%) which
                include Stripe processing fees (2.9% + $0.31). Actual costs may
                vary based on your Stripe account configuration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Get Paid Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white" data-id="element-1871">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center" data-id="element-1872">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-6" data-id="element-1873">
            Get paid as you go - no waiting for your funds!
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto" data-id="element-1874">
            With RaceLookup you keep your cash flow healthy. Connect Stripe and
            deposits flow straight into your account. No delays, no holding
            periods, no surprises.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1875">
            <Link to="/create-account" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-base" data-id="element-1876">
              Create Your Free Member ID
            </Link>
            <Link to="/sign-in" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:border-[#01AAE7] hover:text-[#01AAE7] transition-all text-base" data-id="element-1877">
              Log In
            </Link>
          </div>
        </div>
      </section>

      {/* Why RaceLookup Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50" data-id="element-1878">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10" data-id="element-1879">
          <div className="text-center mb-12" data-id="element-1880">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4" data-id="element-1881">
              Why RaceLookup?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1882">
              Built by race professionals for race professionals. From pricing
              to features, every part of the platform is engineered to keep your
              event running smoothly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1883">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-id="element-1884">
              <div className="flex items-start gap-4 mb-4" data-id="element-1885">
                <div className="w-12 h-12 bg-gradient-to-br from-[#01AAE7] to-[#0199d6] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1886">
                  <DollarSign className="h-6 w-6 text-white" data-id="element-1887" />
                </div>
                <div data-id="element-1888">
                  <h3 className="text-xl font-black text-gray-900 mb-2" data-id="element-1889">
                    1. Exceptional Pricing
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1890">
                    Compare our pricing to other platforms, and you'll see the
                    difference. RaceLookup offers competitive rates without
                    sacrificing quality. Maximize your budget and keep more of
                    your race revenue.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-id="element-1891">
              <div className="flex items-start gap-4 mb-4" data-id="element-1892">
                <div className="w-12 h-12 bg-gradient-to-br from-[#01AAE7] to-[#0199d6] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1893">
                  <Zap className="h-6 w-6 text-white" data-id="element-1894" />
                </div>
                <div data-id="element-1895">
                  <h3 className="text-xl font-black text-gray-900 mb-2" data-id="element-1896">
                    2. Designed for the Pros
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1897">
                    RaceLookup is optimized for the real-world needs of race
                    directors and event managers. We understand the demands of
                    race day and created a platform that makes it easier to
                    manage every detail.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-id="element-1898">
              <div className="flex items-start gap-4 mb-4" data-id="element-1899">
                <div className="w-12 h-12 bg-gradient-to-br from-[#01AAE7] to-[#0199d6] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1900">
                  <Clock className="h-6 w-6 text-white" data-id="element-1901" />
                </div>
                <div data-id="element-1902">
                  <h3 className="text-xl font-black text-gray-900 mb-2" data-id="element-1903">
                    3. Intuitive and Easy-to-Use
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1904">
                    Spend less time navigating complex systems and more time
                    focusing on your event. From registration to race day,
                    managing your race has never been simpler.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-id="element-1905">
              <div className="flex items-start gap-4 mb-4" data-id="element-1906">
                <div className="w-12 h-12 bg-gradient-to-br from-[#01AAE7] to-[#0199d6] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1907">
                  <Check className="h-6 w-6 text-white" data-id="element-1908" />
                </div>
                <div data-id="element-1909">
                  <h3 className="text-xl font-black text-gray-900 mb-2" data-id="element-1910">
                    4. Comprehensive Features
                  </h3>
                  <ul className="text-gray-600 leading-relaxed space-y-2" data-id="element-1911">
                    <li className="flex items-start gap-2" data-id="element-1912">
                      <Check className="h-5 w-5 text-[#01AAE7] flex-shrink-0 mt-0.5" data-id="element-1913" />
                      <span data-id="element-1914">
                        Customizable event pages with branded, dynamic content
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1915">
                      <Check className="h-5 w-5 text-[#01AAE7] flex-shrink-0 mt-0.5" data-id="element-1916" />
                      <span data-id="element-1917">
                        Real-time results to keep runners and spectators engaged
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1918">
                      <Check className="h-5 w-5 text-[#01AAE7] flex-shrink-0 mt-0.5" data-id="element-1919" />
                      <span data-id="element-1920">
                        Flexible registration options including teams and
                        discounts
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1921">
                      <Check className="h-5 w-5 text-[#01AAE7] flex-shrink-0 mt-0.5" data-id="element-1922" />
                      <span data-id="element-1923">
                        Integrated timing solutions with seamless Race Result
                        connectivity
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Feature 5 - Full Width */}
            <div className="md:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-id="element-1924">
              <div className="flex items-start gap-4 mb-4" data-id="element-1925">
                <div className="w-12 h-12 bg-gradient-to-br from-[#01AAE7] to-[#0199d6] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1926">
                  <Shield className="h-6 w-6 text-white" data-id="element-1927" />
                </div>
                <div data-id="element-1928">
                  <h3 className="text-xl font-black text-gray-900 mb-2" data-id="element-1929">
                    5. No Hidden Fees
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1930">
                    Transparency is key. With RaceLookup, what you see is what
                    you get – no surprise fees or upcharges. You'll know exactly
                    what you're paying for upfront.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#0E578E] to-[#01AAE7] relative overflow-hidden" data-id="element-1931">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1932">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-1933"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-1934"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10 text-center" data-id="element-1935">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6" data-id="element-1936">
            Join the RaceLookup Family
          </h2>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-8 max-w-3xl mx-auto" data-id="element-1937">
            Your next event deserves a partner built for speed, flexibility, and
            transparency. We're ready when you are.
          </p>

          <Link to="/create-account" className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0E578E] font-bold rounded-xl hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 text-lg" data-id="element-1938">
            <span data-id="element-1939">Start Listing Your Event</span>
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-1940" />
          </Link>
        </div>
      </section>
    </div>;
}